"use client";
import { Translate } from "@/components/Translate";
import { useMutationObserverCallback } from "@/hooks/useMutationObserver";
import type { ProductDetails } from "@/lib/centra";
import { addLeadingSlash } from "@frend-digital/utils";
import React, { useCallback, useState } from "react";
import styles from "../Reviews/Reviews.module.css";
export const ProductAttributes = ({
	product,
	slug,
}: { product: ProductDetails; slug: string[] }) => {
	// Lipscore mounts into this div asynchronously and self-hides its own
	// content (adds its own `lipscore-hidden` class) once it determines there
	// are no votes on any attribute - the attributes array itself is non-empty
	// even then (it lists the question types, not just answered ones), so
	// `hasVotes` (not array length) is the right "is there anything to show"
	// signal. Keeping the wrapper in the layout unconditionally left a
	// permanent 32px gap on each side for products with no votes, so hide it
	// from layout until there's content.
	const [hasVotes, setHasVotes] = useState(false);

	const ref = useMutationObserverCallback<HTMLDivElement>(
		{ attributes: true, attributeFilter: ["data-ls-product-attributes"] },
		useCallback((mutations) => {
			mutations.forEach((record) => {
				const data =
					(record.target as HTMLDivElement).getAttribute("data-ls-product-attributes") || "";

				try {
					const attributes = JSON.parse(data.replace(/&quot;/g, '"'));
					const hasAnyVotes = attributes.some((attr: { votes: number }) => attr.votes > 0);
					setHasVotes(hasAnyVotes);
				} catch {
					setHasVotes(false);
				}
			});
		}, []),
	);

	return (
		<div className={hasVotes ? undefined : styles.hidden}>
			{hasVotes && (
				<p className={styles.reviewList} data-size-question>
					<Translate>Customer sizing feedback:</Translate>
				</p>
			)}
			<div
				ref={ref}
				className="lipscore-attributes-distribution"
				data-ls-product-name={product.name}
				data-ls-brand="Viking"
				data-ls-product-id={product.centraProductNumber}
				data-ls-product-url={`https://vikingfootwear.com${addLeadingSlash(slug.join("/"))}`}
				data-ls-variant-id={product.displayItemId}
				data-ls-variant-name={product.variantName}
				data-ls-sku={product.sku}
				data-ls-description={product.shortDescription}
				data-ls-image-url={product.media.at(0)?.href}
				data-ls-price={product.priceAsNumber}
				data-ls-price-currency={product.price?.match(/[A-Z]{3}$/)?.[0]}
				data-ls-category={product.canonicalUri}
			/>
		</div>
	);
};
